import React, { useRef, Children } from 'react';
import { Button } from './ui/Button';
import { Printer, MapPin, Phone, Globe } from 'lucide-react';
type CertificateProps = {
  titleEn: string;
  titleUa: string;
  subtitleEn?: string;
  subtitleUa?: string;
  isMonetary?: boolean;
};
function CertificateCard({
  titleEn,
  titleUa,
  subtitleEn,
  subtitleUa,
  isMonetary
}: CertificateProps) {
  return <div className="certificate-item relative bg-white text-gray-800 overflow-hidden print:border-none border border-gray-200 shadow-sm print:shadow-none mb-8 print:mb-0" data-id="element-267" data-mp="18m8ynxr5d2wq">
      {/* Dimensions for screen (approx A5 ratio) and print (exact half A4) */}
      <div className="w-full aspect-[1.414/1] print:aspect-auto print:h-[148.5mm] print:w-[210mm] relative flex flex-col" data-id="element-268" data-mp="1hldu2v7ewg4o">
        {/* Decorative Border */}
        <div className="absolute inset-0 p-4 pointer-events-none z-0" data-id="element-269" data-mp="oyjeocpgc6y2">
          <div className="w-full h-full border-4 border-double border-teal-900/20 rounded-sm" data-id="element-270" data-mp="owf9lrfth1w7">
            <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-teal-600 rounded-tl-xl" data-id="element-271" data-mp="tv9tkwpiswhw" />
            <div className="absolute top-0 right-0 w-16 h-16 border-t-4 border-r-4 border-teal-600 rounded-tr-xl" data-id="element-272" data-mp="lyiqp3cif1y1" />
            <div className="absolute bottom-0 left-0 w-16 h-16 border-b-4 border-l-4 border-teal-600 rounded-bl-xl" data-id="element-273" data-mp="h7aha7b6jgkv" />
            <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 border-teal-600 rounded-br-xl" data-id="element-274" data-mp="c7o5817386dn" />
          </div>
        </div>

        {/* Elegant Watermark — Stylized Tooth */}
        <div className="absolute inset-0 pointer-events-none z-0 flex items-center justify-center overflow-hidden" data-id="element-275" data-mp="29i4hmx0p6ad">
          <svg width="280" height="320" viewBox="0 0 280 320" fill="none" xmlns="http://www.w3.org/2000/svg" className="opacity-[0.035]" data-id="element-276" data-mp="3uf5ag96lcksh">
            {/* Tooth crown */}
            <path d="M140 20 C90 20 50 55 50 100 C50 130 60 150 75 165 C85 175 90 190 88 210 L82 280 C80 295 90 310 105 310 C115 310 122 302 125 290 L140 240 L155 290 C158 302 165 310 175 310 C190 310 200 295 198 280 L192 210 C190 190 195 175 205 165 C220 150 230 130 230 100 C230 55 190 20 140 20 Z" fill="currentColor" className="text-teal-700" data-id="element-277" data-mp="1no5rx9kvuun3" />
            {/* Tooth roots */}
            <path d="M105 310 C100 310 96 305 97 300 L82 280" stroke="currentColor" strokeWidth="2" className="text-teal-700" fill="none" data-id="element-278" data-mp="22rpbnk2lwbxa" />
            <path d="M175 310 C180 310 184 305 183 300 L198 280" stroke="currentColor" strokeWidth="2" className="text-teal-700" fill="none" data-id="element-279" data-mp="2uhal4lz4vatz" />
            {/* Shine highlight on crown */}
            <path d="M110 60 C115 50 130 45 140 48" stroke="currentColor" strokeWidth="4" strokeLinecap="round" className="text-teal-500" opacity="0.5" fill="none" data-id="element-280" data-mp="15242qwe2w0au" />
            {/* Subtle cross line on crown */}
            <path d="M70 105 Q140 125 210 105" stroke="currentColor" strokeWidth="1.5" className="text-teal-600" opacity="0.4" fill="none" data-id="element-281" data-mp="xj4p77dat2ze" />
          </svg>
        </div>

        {/* Elegant Background Pattern */}
        <div className="absolute inset-0 pointer-events-none z-0 opacity-[0.04]" data-id="element-282" data-mp="iv045wqrwfdg">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-283" data-mp="eupvgcbljauw">
            <defs data-id="element-284" data-mp="sll87zp7uc3r">
              <pattern id="cert-pattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse" data-id="element-285" data-mp="sm4g0nl5jk42">
                <path d="M20 0 L40 20 L20 40 L0 20 Z" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-teal-800" data-id="element-286" data-mp="25w7mrkhniq" />
                <circle cx="20" cy="20" r="1.5" fill="currentColor" className="text-teal-700" data-id="element-287" data-mp="dqg2d3cf5m99" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#cert-pattern)" data-id="element-288" data-mp="nic2sl1z9ogn" />
          </svg>
        </div>

        {/* Content */}
        <div className="relative z-10 flex-grow flex flex-col p-8 sm:p-10 print:p-8 h-full justify-between" data-id="element-289" data-mp="rkqgkqqshy12">
          {/* Header */}
          <div className="flex justify-between items-start" data-id="element-290" data-mp="11gac3c1fyupa">
            <div className="flex items-center gap-3" data-id="element-291" data-mp="11epj3qurzjtj">
              <img src="https://cdn.magicpatterns.com/uploads/oHJPxkkc7f6GgU5uyo3LQX/ChatGPT_Image_Feb_5,_2026,_03_12_40_PM.png" alt="Dentico Clinic Logo" className="h-12 sm:h-14 w-auto object-contain" data-id="element-292" data-mp="16eburvuygs08" />
              <div className="border-l border-gray-300 pl-3 ml-1" data-id="element-293" data-mp="1pbn82wrnv0j9">
                <span className="text-lg sm:text-xl font-bold text-gray-900 tracking-tight block leading-none mb-1" data-id="element-294" data-mp="1pdzxtw3nuf6v">
                  Dentico<span className="text-teal-600" data-id="element-295" data-mp="1gjbrs3q9gth0">Clinic</span>
                </span>
                <p className="text-[11px] sm:text-xs text-gray-600 uppercase tracking-widest leading-tight" data-id="element-296" data-mp="1gh7hn41rqy8a">
                  Private Dentistry
                </p>
                <p className="text-[11px] sm:text-xs text-gray-600 uppercase tracking-widest leading-tight" data-id="element-297" data-mp="28a7uwcmirjr3">
                  London, Angel
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-298" data-mp="1z8i1x2czocr7">
              <h1 className="text-xl sm:text-2xl font-serif text-teal-900 font-bold tracking-wide uppercase" data-id="element-299" data-mp="1uayog4o4840b">
                {isMonetary ? 'Gift Voucher' : 'Gift Certificate'}
              </h1>
              <p className="text-sm text-teal-700 font-medium italic" data-id="element-300" data-mp="ivt83xv2a6y8">
                {isMonetary ? 'Подарунковий ваучер' : 'Подарунковий сертифікат'}
              </p>
            </div>
          </div>

          {/* Main Body */}
          <div className="flex-grow flex flex-col justify-center py-2" data-id="element-301" data-mp="pcbm2vyyp0zu">
            {/* Service Name or Monetary Amount */}
            <div className="border-b-2 border-teal-100 pb-4 mb-4" data-id="element-302" data-mp="bbsetr2blyyq">
              {isMonetary ? <div data-id="element-303" data-mp="g9twq8o0fs8m">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider mb-1 font-medium" data-id="element-304" data-mp="gdhq97tggenc">
                    Value / Сума
                  </p>
                  <div className="flex items-baseline text-3xl font-serif font-bold text-teal-900" data-id="element-305" data-mp="6e90kr5mhm6s">
                    <span data-id="element-306" data-mp="3os85qlwr27ar">£</span>
                    <div contentEditable suppressContentEditableWarning className="editable-field min-w-[120px] border-b-2 border-dashed border-teal-300 focus:outline-none focus:border-teal-500 ml-1 px-1 text-teal-900" data-id="element-307" data-mp="3op3ux5os4db4" />
                  </div>
                </div> : <>
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider mb-1 font-medium" data-id="element-308" data-mp="306kbm5ekhz0d">
                    Service / Послуга
                  </p>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight mb-1" data-id="element-309" data-mp="3a4rmq7m6dmug">
                    {titleEn}
                  </h2>
                  <p className="text-sm text-gray-600 italic" data-id="element-310" data-mp="357hs8djxikzr">{titleUa}</p>
                  {(subtitleEn || subtitleUa) && <div className="mt-1 text-xs text-teal-700 font-medium" data-id="element-311" data-mp="3e6cuxfnrlcyb">
                      {subtitleEn} {subtitleUa && `/ ${subtitleUa}`}
                    </div>}
                </>}
            </div>

            {/* Editable Fields Grid */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-4" data-id="element-312" data-mp="5q9otecavwc3">
              {/* To */}
              <div data-id="element-313" data-mp="5nx9l8xxg02q">
                <label className="block text-[11px] text-gray-500 uppercase tracking-wider mb-1 font-medium" data-id="element-314" data-mp="1oy7dw3kyhez">
                  Presented to / Кому
                </label>
                <div contentEditable suppressContentEditableWarning className="editable-field w-full border-b-2 border-dashed border-gray-300 text-base font-serif text-gray-900 focus:outline-none focus:border-teal-500 pb-1 min-h-[1.6em]" data-id="element-315" data-mp="185qir8iv230z" />
              </div>

              {/* From */}
              <div data-id="element-316" data-mp="pe7j9bo1p73p">
                <label className="block text-[11px] text-gray-500 uppercase tracking-wider mb-1 font-medium" data-id="element-317" data-mp="pepp900iezot">
                  From / Від кого
                </label>
                <div contentEditable suppressContentEditableWarning className="editable-field w-full border-b-2 border-dashed border-gray-300 text-base font-serif text-gray-900 focus:outline-none focus:border-teal-500 pb-1 min-h-[1.6em]" data-id="element-318" data-mp="ydkry24chrnd" />
              </div>

              {/* Date & Valid */}
              <div className="flex gap-4 col-span-2 sm:col-span-1" data-id="element-319" data-mp="34qzlpzoqvp1">
                <div className="flex-1" data-id="element-320" data-mp="33y1fdesleii">
                  <label className="block text-[10px] text-gray-500 uppercase tracking-wider mb-1 font-medium" data-id="element-321" data-mp="3uba82yi1u9k6">
                    Date / Дата
                  </label>
                  <div contentEditable suppressContentEditableWarning className="editable-field w-full border-b border-dashed border-gray-300 text-sm font-mono text-gray-700 focus:outline-none focus:border-teal-500 pb-0.5 min-h-[1.5em]" data-id="element-322" data-mp="3qbjkf3ntk9jv" />
                </div>
                <div className="flex-1" data-id="element-323" data-mp="36ky23t6d6mw8">
                  <label className="block text-[10px] text-gray-500 uppercase tracking-wider mb-1 font-medium" data-id="element-324" data-mp="36lzcrwjgx7df">
                    Valid Until / Дійсний до
                  </label>
                  <div contentEditable suppressContentEditableWarning className="editable-field w-full border-b border-dashed border-gray-300 text-sm font-mono text-gray-700 focus:outline-none focus:border-teal-500 pb-0.5 min-h-[1.5em]" data-id="element-325" data-mp="2xjgdtniszewg" />
                </div>
              </div>

              {/* Certificate No */}
              <div className="col-span-2 sm:col-span-1" data-id="element-326" data-mp="kh6i0jq4s1kf">
                <label className="block text-[10px] text-gray-500 uppercase tracking-wider mb-1 font-medium" data-id="element-327" data-mp="fj4xrvgyxw7d">
                  Certificate No. / Номер
                </label>
                <div contentEditable suppressContentEditableWarning className="editable-field w-full border-b border-dashed border-gray-300 text-sm font-mono text-gray-700 focus:outline-none focus:border-teal-500 pb-0.5 min-h-[1.5em]" data-id="element-328" data-mp="3o114aucgd0t5">
                  DC-{new Date().getFullYear()}-
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-4 pt-3 border-t border-gray-200 flex justify-between items-end gap-4" data-id="element-329" data-mp="188lqyvcexh4n">
            {/* Contact Info */}
            <div className="text-[11px] text-gray-600 space-y-0.5" data-id="element-330" data-mp="2q4457n3i1log">
              <div className="flex items-center gap-1.5" data-id="element-331" data-mp="2v5l4xfa8z23g">
                <MapPin className="w-3 h-3 text-teal-600 flex-shrink-0" data-id="element-332" data-mp="2h4qw4vjaez49" />
                <span data-id="element-333" data-mp="2h6lkkjtrp9d3">360–364 City Road, London EC1V 2PY</span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-334" data-mp="2m4dka00nlzru">
                <Phone className="w-3 h-3 text-teal-600 flex-shrink-0" data-id="element-335" data-mp="39swgviacco43" />
                <span data-id="element-336" data-mp="39vsfz9xxdiu4">+44 7345 197820 (WhatsApp)</span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-337" data-mp="3du89lwoihpgi">
                <Globe className="w-3 h-3 text-teal-600 flex-shrink-0" data-id="element-338" data-mp="304el5fgx577b" />
                <span data-id="element-339" data-mp="352ptflrkf9nf">denticoclinic.co.uk</span>
              </div>
            </div>

            {/* Terms */}
            <div className="text-[10px] text-gray-500 text-right max-w-[220px] leading-snug" data-id="element-340" data-mp="3ssk5w27z2w75">
              <p data-id="element-341" data-mp="1dcrwvgpdruu">
                Valid for one visit. Non-transferable. Please present this
                certificate at your appointment.
              </p>
              <p className="mt-0.5" data-id="element-342" data-mp="b8tqyqrlru07">
                Book: denticoclinic.patientportal.onl/bookings
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
export function GiftCertificate() {
  const handlePrint = () => {
    window.print();
  };
  const certificates = [{
    titleEn: 'Professional Dental Hygiene',
    titleUa: 'Професійна гігієна: чистка + Airflow',
    subtitleEn: 'Scale & Polish + Airflow'
  }, {
    titleEn: 'Clinical Teeth Whitening',
    titleUa: 'Клінічне відбілювання зубів'
  }, {
    titleEn: 'Hygiene + Whitening Combo',
    titleUa: 'Чистка + Airflow + відбілювання',
    subtitleEn: 'Scale & Polish + Airflow + Whitening'
  }, {
    titleEn: 'Home Whitening Kit',
    titleUa: 'Домашній набір для відбілювання'
  }, {
    titleEn: 'Composite Bonding',
    titleUa: 'Композитний бондинг'
  }, {
    titleEn: 'E.max Veneer (per tooth)',
    titleUa: 'Вінір E.max (1 зуб)'
  }, {
    titleEn: 'Smile Makeover Consultation',
    titleUa: 'Консультація Smile Makeover'
  }, {
    titleEn: 'Hydrafacial',
    titleUa: 'Гідрафейшл'
  }, {
    titleEn: 'Botox Treatment',
    titleUa: 'Процедура ботокс'
  }, {
    titleEn: 'New Patient Exam (incl. 2 X-Rays)',
    titleUa: 'Огляд нового пацієнта (вкл. 2 знімки)'
  }, {
    titleEn: "Children's Dental Hygiene + Fluoride",
    titleUa: 'Дитяча гігієна + фторування'
  }, {
    titleEn: 'Gift Voucher',
    titleUa: 'Подарунковий ваучер',
    isMonetary: true
  }];
  return <div className="min-h-screen bg-gray-100 p-4 sm:p-8 print:p-0 print:bg-white" data-id="element-343" data-mp="1b9f1oq59lpdl">
      {/* Controls - Hidden when printing */}
      <div className="max-w-5xl mx-auto mb-8 flex flex-col sm:flex-row gap-4 items-center justify-between print:hidden" data-id="element-344" data-mp="dut2qh2kgaqc">
        <div data-id="element-345" data-mp="irt80prld9ge">
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-346" data-mp="ir9qha5usn2w">
            Gift Certificates Template
          </h1>
          <p className="text-gray-600 text-sm" data-id="element-347" data-mp="2kfxgufnjy2jm">
            Ready to print. Layout is optimized for 2 certificates per A4 page.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2" data-id="element-348" data-mp="2u94t8kgpmit7">
          <Button onClick={handlePrint} className="gap-2 shadow-lg" data-id="element-349" data-mp="2phd48ed7cl56">
            <Printer className="w-4 h-4" data-id="element-350" data-mp="2pg20gejrbde2" />
            Print All Certificates
          </Button>
          <p className="text-xs text-gray-500" data-id="element-351" data-mp="344g06glvmlqe">
            Tip: Set paper size to <strong data-id="element-352" data-mp="2z64ri97bih9r">A4</strong> and enable{' '}
            <strong data-id="element-353" data-mp="2z9irxbwf8gek">Background graphics</strong>
          </p>
        </div>
      </div>

      {/* Certificates Grid */}
      <div className="max-w-[210mm] mx-auto print:max-w-none print:w-full" data-id="element-354" data-mp="38848j1ic1lcp">
        {certificates.map((cert, idx) => <CertificateCard key={idx} {...cert} data-id="element-355" data-mp="1is4ub5zux5q7" data-mpi={idx} />)}
      </div>

      {/* Print Styles */}
      <style data-id="element-356" data-mp="1dumuq67qc2uv">{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 0;
          }
          html, body {
            margin: 0;
            padding: 0;
            background: white;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .print\\:border-none {
            border: none !important;
          }
          .print\\:p-0 {
            padding: 0 !important;
          }
          .print\\:bg-white {
            background: white !important;
          }
          .print\\:max-w-none {
            max-width: none !important;
          }
          .print\\:w-full {
            width: 100% !important;
          }
          
          /* Ensure exactly 2 per page */
          .certificate-item {
            break-inside: avoid;
            page-break-inside: avoid;
            height: 148.5mm; /* Exact half of A4 height */
            width: 210mm;    /* Exact A4 width */
            margin: 0;
            padding: 0;
            display: flex;
            flex-direction: column;
          }
          
          /* Force page break after every 2nd item if needed, 
             though natural flow should handle it with fixed height */
          .certificate-item:nth-child(2n) {
            page-break-after: always;
          }
        }
      `}</style>
    </div>;
}